/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import type { DeprecatedDropdownMenuOption } from "@octopusdeploy/design-system-octopus-components";
import type { ChannelResource, DeploymentProcessResource, DeploymentTargetResource, EnvironmentV2Resource, LifecycleResource, ProjectResource, ResourceCollection, TenantResource, VariableResource, VariableSetResource, } from "@octopusdeploy/octopus-server-client";
import { mapFromEnvironmentResourceToEnvironmentV2Resource, Permission, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { flatMap } from "lodash";
import type { FC } from "react";
import { useProjectContext } from "~/areas/projects/context";
import type { WithProjectContextInjectedProps } from "~/areas/projects/context/withProjectContext";
import VariableDisplayer, { mergeAndSortVariables, type FilteredVariable, type ValueWithSource, type VariableWithSource } from "~/areas/variables/VariableDisplayer";
import { convertVariableResourcesToVariablesWithSource } from "~/areas/variables/convertVariableResourcesToVariablesWithSource";
import { repository } from "~/clientInstance";
import AdvancedFilterLayout from "~/components/AdvancedFilterLayout";
import type { FilterSection } from "~/components/AdvancedFilterLayout";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { SimplePagingList } from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import { useEphemeralEnvironmentsEnabled } from "../../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { VcsError } from "../../VersionControl/VcsError";
import { VariableExpressionTesterTab } from "./VariableExpressionTesterTab";
import VariablePreviewSummary from "./VariablePreviewSummary";
import { VariableScopeSelector } from "./VariableScopeSelector";
interface VariableListProps {
    variables: VariableResource[];
}
class VariableList extends SimplePagingList<VariableResource> {
}
const Variables: FC<VariableListProps> = (props) => {
    const onRow = (variable: VariableResource) => [<b>{variable.Name}</b>, variable.Value];
    return <VariableList items={props.variables} onRow={onRow} empty={<span>No variables have been added</span>}/>;
};
Variables.displayName = "Variables";
interface VariablePreviewFilter {
    environmentId?: string;
    targetId?: string;
    channelId?: string;
    tenantId?: string;
    actionId?: string;
    machineId?: string;
    showOctopus: boolean;
    filterEmptyValues: boolean;
    processType: "deployment" | "runbook";
    runbookId?: string;
}
function createDefaultVariablePreviewFilter(): VariablePreviewFilter {
    return {
        showOctopus: false,
        filterEmptyValues: false,
        processType: "deployment",
    };
}
class FilterLayout extends AdvancedFilterLayout<VariablePreviewFilter> {
}
interface SharedVariablePreviewPageState extends DataBaseComponentState {
    variableNameFilter: string;
    environmentId?: string;
    targetId?: string;
    channelId?: string;
    tenantId?: string;
    actionId?: string;
    machineId?: string;
    project?: ProjectResource;
    environments?: EnvironmentV2Resource[];
    targets?: DeploymentTargetResource[];
    channels?: DeprecatedDropdownMenuOption[];
    tenants?: DeprecatedDropdownMenuOption[];
    actions?: DeprecatedDropdownMenuOption[];
    machines?: DeprecatedDropdownMenuOption[];
    variables?: VariableSetResource;
    showOctopus: boolean;
    filterEmptyValues: boolean;
    currentTab: "preview" | "test";
    processType: "deployment" | "runbook";
    runbookId?: string;
    runbooks?: DeprecatedDropdownMenuOption[];
    filter: VariablePreviewFilter;
    isAdvancedFiltersVisible: boolean;
}
type SharedVariablePreviewPageInternalProps = WithProjectContextInjectedProps & {
    ephemeralEnvironmentsAreEnabled: boolean;
};
const pageTitle = "Variable Preview";
class SharedVariablePreviewPageInternal extends DataBaseComponent<SharedVariablePreviewPageInternalProps, SharedVariablePreviewPageState> {
    constructor(props: SharedVariablePreviewPageInternalProps) {
        super(props);
        this.state = {
            showOctopus: false,
            filterEmptyValues: false,
            variableNameFilter: null!,
            currentTab: "preview",
            processType: "deployment",
            filter: createDefaultVariablePreviewFilter(),
            isAdvancedFiltersVisible: false,
        };
    }
    async componentDidMount() {
        await this.loadData();
    }
    async componentDidUpdate(prevProps: SharedVariablePreviewPageInternalProps) {
        if (prevProps.projectContext.state.gitRef !== this.props.projectContext.state.gitRef) {
            await this.loadVariables();
        }
    }
    render() {
        if (!this.state.variables || !this.state.project) {
            return (<PageContent header={{ title: pageTitle }} busy={Promise.resolve()} errors={this.errors}>
                    {undefined}
                </PageContent>);
        }
        const summary = (<VariablePreviewSummary tenantedDeploymentMode={this.state.project && this.state.project.TenantedDeploymentMode} environments={this.state.environments!} environmentId={this.state.environmentId!} tenants={this.state.tenants!} tenantId={this.state.tenantId!} actions={this.state.actions!} actionId={this.state.actionId!} channels={this.state.channels!} channelId={this.state.channelId!} machines={this.state.machines!} machineId={this.state.machineId!}/>);
        const variableSections = mergeAndSortVariables(this.getVariables(), this.state.variables.ScopeValues);
        const filteredVariableSections = variableSections.map((v) => {
            return this.mapToFilteredVariable(v);
        });
        const previewContent = this.state.variables && this.state.currentTab === "preview" && (<FilterLayout key="variable-preview-filters" filter={this.state.filter} defaultFilter={createDefaultVariablePreviewFilter()} additionalHeaderFilters={[<FilterSearchBox placeholder="By variable name" value={this.state.variableNameFilter} onChange={this.handleFilterChanged}/>]} onFilterReset={this.handleFilterReset} filterSections={this.filterSections()} extendContentToEdges={true} hideDivider={false} overflowXHidden={true} initiallyShowFilter={this.state.isAdvancedFiltersVisible} onToggleFilter={this.handleAdvancedFiltersToggle} renderContent={() => (<VariableDisplayer availableScopes={this.state.variables!.ScopeValues} variableSections={[filteredVariableSections]} doBusyTask={this.doBusyTask} hideSource={true} hideScope={false} isDisplayingFullWidth={false} ephemeralEnvironmentsAreEnabled={this.props.ephemeralEnvironmentsAreEnabled}/>)}/>);
        const variableNames = this.state.variables?.Variables.map((v) => v.Name).sort((a, b) => {
            if (a.startsWith("Octopus.") && !b.startsWith("Octopus.")) {
                return 1;
            }
            if (!a.startsWith("Octopus.") && b.startsWith("Octopus.")) {
                return -1;
            }
            return a.localeCompare(b);
        }) || [];
        const testContent = this.state.variables && this.state.currentTab === "test" && (<div style={{ paddingTop: "1rem" }}>
                <VariableExpressionTesterTab project={this.state.project!} environments={this.state.environments!} environmentId={this.state.environmentId} tenants={this.state.tenants!} tenantId={this.state.tenantId} actions={this.state.actions!} actionId={this.state.actionId} channels={this.state.channels!} channelId={this.state.channelId} machines={this.state.machines!} machineId={this.state.machineId} gitRef={this.props.projectContext.state.gitRef?.CanonicalName} showOctopus={this.state.showOctopus} filterEmptyValues={this.state.filterEmptyValues} variableNames={variableNames} processType={this.state.processType} runbooks={this.state.runbooks} runbookId={this.state.runbookId} onEnvironmentChanged={this.handleEnvironmentChanged} onTenantChanged={this.handleTenantChanged} onActionChanged={this.handleActionChanged} onChannelChanged={this.handleChannelChanged} onMachineChanged={this.handleMachineChanged} onShowOctopusChanged={this.handleShowOctopusChanged} onFilterEmptyValuesChanged={this.handleFilterEmptyValuesChanged} onProcessTypeChanged={this.handleProcessTypeChanged} onRunbookChanged={this.handleRunbookChanged}/>
            </div>);
        //TODO: FEF - Deal with sidebar layout usage, which looks like its usage is more like advanced filters, but not quite.
        return (<PageContent header={{ title: pageTitle }} busy={this.state.busy} errors={this.errors} currentTab={this.state.currentTab} onTabChanged={this.handleTabChanged} tabs={[
                {
                    label: "Preview",
                    value: "preview",
                    content: previewContent,
                },
                {
                    label: "Test",
                    value: "test",
                    content: testContent,
                },
            ]}/>);
    }
    private mapToFilteredVariable(variable: VariableWithSource): FilteredVariable {
        return {
            name: variable.name,
            variableMessages: null!,
            values: variable.values.map((x) => {
                const value = x as ValueWithSource;
                return {
                    ...value,
                    messages: null!,
                };
            }),
        };
    }
    private handleFilterChanged = (variableNameFilter: string) => {
        this.setState({ variableNameFilter });
    };
    private handleFilterReset = (resetFilter: VariablePreviewFilter) => {
        this.setState({
            filter: resetFilter,
            environmentId: resetFilter.environmentId,
            targetId: resetFilter.targetId,
            channelId: resetFilter.channelId,
            tenantId: resetFilter.tenantId,
            actionId: resetFilter.actionId,
            machineId: resetFilter.machineId,
            showOctopus: resetFilter.showOctopus,
            filterEmptyValues: resetFilter.filterEmptyValues,
            processType: resetFilter.processType,
            runbookId: resetFilter.runbookId,
        });
    };
    private createFilterFromState = (state: SharedVariablePreviewPageState): VariablePreviewFilter => {
        return {
            environmentId: state.environmentId,
            targetId: state.targetId,
            channelId: state.channelId,
            tenantId: state.tenantId,
            actionId: state.actionId,
            machineId: state.machineId,
            showOctopus: state.showOctopus,
            filterEmptyValues: state.filterEmptyValues,
            processType: state.processType,
            runbookId: state.runbookId,
        };
    };
    private filterSections = (): FilterSection[] => {
        return [
            {
                render: (<VariableScopeSelector project={this.state.project!} environments={this.state.environments!} environmentId={this.state.environmentId} tenants={this.state.tenants!} tenantId={this.state.tenantId} actions={this.state.actions!} actionId={this.state.actionId} channels={this.state.channels!} channelId={this.state.channelId} machines={this.state.machines!} machineId={this.state.machineId} showOctopus={this.state.showOctopus} filterEmptyValues={this.state.filterEmptyValues} processType={this.state.processType} runbooks={this.state.runbooks} runbookId={this.state.runbookId} noIndent={true} onEnvironmentChanged={this.handleEnvironmentChanged} onTenantChanged={this.handleTenantChanged} onActionChanged={this.handleActionChanged} onChannelChanged={this.handleChannelChanged} onMachineChanged={this.handleMachineChanged} onShowOctopusChanged={this.handleShowOctopusChanged} onFilterEmptyValuesChanged={this.handleFilterEmptyValuesChanged} onProcessTypeChanged={this.handleProcessTypeChanged} onRunbookChanged={this.handleRunbookChanged}/>),
            },
        ];
    };
    private async loadEnvironmentsFromLifecycle(lifecycle: LifecycleResource) {
        if (!isAllowed({ permission: Permission.EnvironmentView, environment: "*" })) {
            return [];
        }
        const environmentIds = flatMap(lifecycle.Phases, (phase) => {
            return phase.AutomaticDeploymentTargets.concat(phase.OptionalDeploymentTargets);
        });
        const args = environmentIds.length === 0 ? undefined : { ids: environmentIds };
        const environments = await repository.Environments.all(args);
        return environments.map(mapFromEnvironmentResourceToEnvironmentV2Resource);
    }
    private async loadEnvironmentsFromParentEnvironment(spaceId: string, parentEnvironmentId: string) {
        if (!isAllowed({ permission: Permission.EnvironmentView, environment: "*" })) {
            return [];
        }
        const environments = await repository.Environments.getV2({ spaceId: spaceId, skip: 0, take: Repository.takeAll, type: ["Parent"] });
        const parentEnvironment = environments.Items.find((e) => e.Id === parentEnvironmentId);
        return parentEnvironment ? [parentEnvironment] : [];
    }
    private async loadData() {
        await this.doBusyTask(async () => {
            const { model: project, projectContextRepository } = this.props.projectContext.state;
            const gitRef = this.props.projectContext.state.gitRef?.CanonicalName;
            const hasProcessViewPermissions = isAllowed({
                permission: Permission.ProcessView,
                project: project.Id,
                tenant: "*",
            });
            const hasRunbookViewPermissions = isAllowed({
                permission: Permission.RunbookView,
                project: project.Id,
                tenant: "*",
            });
            const [deploymentProcess, channels, lifecycle, tenants, runbooks] = await Promise.all([
                hasProcessViewPermissions ? projectContextRepository.DeploymentProcesses.get() : Promise.resolve<DeploymentProcessResource>(null!),
                hasProcessViewPermissions ? repository.Projects.getChannels(project, 0, 1000) : Promise.resolve<ResourceCollection<ChannelResource>>(null!),
                repository.Lifecycles.get(project.LifecycleId),
                isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all({ projectId: project.Id }) : Promise.resolve<TenantResource[]>([]),
                hasRunbookViewPermissions ? repository.Runbooks.all({ projectIds: [project.Id] }) : Promise.resolve([]),
            ]);
            const environments = await this.loadEnvironmentsFromLifecycle(lifecycle);
            const tenantId = tenants && tenants.length > 0 ? tenants[0].Id : null!;
            const environmentId = environments && environments.length > 0 ? environments[0].Id : null!;
            const actionId = deploymentProcess && deploymentProcess.Steps.length > 0 ? deploymentProcess.Steps[0].Actions[0].Id : null!;
            const channelId = channels && channels.Items.length > 0 ? channels.Items[0].Id : null!;
            const variablesPromise = repository.Variables.preview(project.Id, null!, actionId, environmentId, null!, channelId, tenantId, gitRef);
            const machinesPromise = this.loadMachinesForEnvironment(environmentId!);
            const [variables, machines] = await Promise.all([variablesPromise, machinesPromise]);
            const actions = variables.ScopeValues.Actions.map((a) => ({ text: a.Name, value: a.Id }));
            const newState = {
                project,
                environments: environments,
                environmentId,
                channels: channels.Items.map((c) => ({ text: c.Name, value: c.Id })),
                channelId,
                tenants: tenants.map((t) => ({ text: t.Name, value: t.Id })),
                tenantId,
                machines,
                actions,
                actionId,
                variables,
                runbooks: runbooks.map((r) => ({ text: r.Name, value: r.Id })),
                processType: this.state.processType === "runbook" && runbooks.length === 0 ? ("deployment" as const) : this.state.processType,
                runbookId: runbooks.length === 0 ? undefined : this.state.runbookId,
            };
            this.setState((prevState) => ({
                ...newState,
                filter: this.createFilterFromState({ ...prevState, ...newState }),
            }));
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    private loadMachinesForEnvironment = async (environmentId: string) => {
        if (!environmentId || !isAllowed({ permission: Permission.MachineView, wildcard: true })) {
            return [];
        }
        const machines = await repository.Machines.list({ environmentIds: environmentId, take: 999 });
        return machines.Items.map((m) => ({ text: m.Name, value: m.Id }));
    };
    private handleEnvironmentChanged = async (environmentId: string | undefined) => {
        await this.doBusyTask(async () => {
            const machines = await this.loadMachinesForEnvironment(environmentId!);
            const machine = machines.find((m) => m.value === this.state.machineId)!;
            const machineId = machine ? machine.value : null!;
            const newState = {
                environmentId,
                machines,
                machineId,
            };
            this.setState({
                ...newState,
                filter: this.createFilterFromState({ ...this.state, ...newState }),
            }, () => {
                this.loadVariables();
            });
        });
    };
    private handleMachineChanged = (machineId: string | undefined) => {
        const newState = { machineId };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        }, () => {
            this.loadVariables();
        });
    };
    private handleChannelChanged = async (channelId: string | undefined) => {
        await this.doBusyTask(async () => {
            const channel = await repository.Channels.getFromProject(this.state.project!, channelId!);
            let environments: EnvironmentV2Resource[] = [];
            if (channel.Type === "Lifecycle") {
                const lifecycleId = channel.LifecycleId ?? this.state.project!.LifecycleId;
                const lifecycle = await repository.Lifecycles.get(lifecycleId);
                environments = await this.loadEnvironmentsFromLifecycle(lifecycle);
            }
            else if (channel.Type === "EphemeralEnvironment") {
                environments = await this.loadEnvironmentsFromParentEnvironment(channel.SpaceId, channel.ParentEnvironmentId!);
            }
            let environmentId = environments.some((x) => x.Id === this.state.environmentId) ? this.state.environmentId : null!;
            if (!environmentId && environments.length === 1) {
                environmentId = environments[0].Id;
                this.handleEnvironmentChanged(environmentId);
            }
            const newState = {
                environments: environments,
                channelId,
                environmentId,
            };
            this.setState({
                ...newState,
                filter: this.createFilterFromState({ ...this.state, ...newState }),
            }, () => {
                this.loadVariables();
            });
        });
    };
    private handleTenantChanged = (tenantId: string | undefined) => {
        const newState = { tenantId };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        }, () => {
            this.loadVariables();
        });
    };
    private handleActionChanged = (actionId: string | undefined) => {
        const newState = { actionId };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        }, () => {
            this.loadVariables();
        });
    };
    private handleFilterEmptyValuesChanged = (showEmptyValues: boolean) => {
        const newState = { filterEmptyValues: showEmptyValues };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        });
    };
    private handleShowOctopusChanged = (showOctopus: boolean) => {
        const newState = { showOctopus };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        });
    };
    private handleTabChanged = (value: string) => {
        if (value === "preview" || value === "test") {
            this.setState({ currentTab: value });
        }
    };
    private handleAdvancedFiltersToggle = async (isVisible: boolean) => {
        this.setState({ isAdvancedFiltersVisible: isVisible });
    };
    private switchToRunbookMode = () => {
        const runbookId = this.state.runbooks && this.state.runbooks.length > 0 ? this.state.runbooks[0].value || undefined : undefined;
        const newState = {
            processType: "runbook" as const,
            runbookId,
            actionId: undefined,
        };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        }, () => {
            this.loadVariables();
        });
    };
    private switchToDeploymentMode = () => {
        const newState = {
            processType: "deployment" as const,
            runbookId: undefined,
        };
        this.setState({
            ...newState,
            filter: this.createFilterFromState({ ...this.state, ...newState }),
        }, () => {
            this.loadActionsForProcess();
        });
    };
    private handleProcessTypeChanged = (processType: "deployment" | "runbook") => {
        if (processType === "runbook") {
            this.switchToRunbookMode();
        }
        else {
            this.switchToDeploymentMode();
        }
    };
    private handleRunbookChanged = async (runbookId: string | undefined) => {
        await this.doBusyTask(async () => {
            const newState = {
                runbookId,
                actionId: undefined,
            };
            this.setState({
                ...newState,
                filter: this.createFilterFromState({ ...this.state, ...newState }),
            }, () => {
                this.loadVariables();
            });
        });
    };
    private loadActionsForProcess = async () => {
        if (this.state.processType === "deployment") {
            await this.loadVariables();
        }
    };
    private getVariables = () => {
        function isEmpty(value?: string | null) {
            return value ? false : true;
        }
        if (!this.state.variables) {
            return [];
        }
        // We don't show the source on the variable preview page. We use this source
        // in the call to convertVariableResourcesToVariablesWithSource so that it
        // has _a_ source, but it's setting everything to project which is not correct.
        const source = {
            projectName: this.state.project!.Name!,
            projectId: this.state.project!.Id!,
            spaceId: this.state.project!.SpaceId,
        };
        const filtered = this.state.variables.Variables.filter((v) => !this.state.filterEmptyValues || isEmpty(v.Value))
            .filter((v) => this.state.showOctopus || !v.Name.startsWith("Octopus."))
            .filter((v) => !this.state.variableNameFilter || this.state.variableNameFilter.length === 0 || v.Name.toLowerCase().includes(this.state.variableNameFilter.toLowerCase()));
        return convertVariableResourcesToVariablesWithSource(filtered, source);
    };
    private loadVariables = async () => {
        const gitRef = this.props.projectContext.state.gitRef?.CanonicalName;
        await this.doBusyTask(async () => {
            const runbookId = this.state.processType === "runbook" ? this.state.runbookId : undefined;
            const variables = await repository.Variables.preview(this.state.project!.Id, runbookId || undefined, this.state.actionId!, this.state.environmentId!, this.state.machineId!, this.state.channelId!, this.state.tenantId!, gitRef);
            const actions = variables.ScopeValues.Actions.map((a) => ({ text: a.Name, value: a.Id }));
            this.setState({
                variables,
                actions,
            });
        }, { timeOperationOptions: timeOperationOptions.forRefresh() });
    };
    static displayName = "SharedVariablePreviewPageInternal";
}
function SharedVariablePreviewPage() {
    const projectContext = useProjectContext();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const spaceNavigation = useSpaceAwareNavigation();
    const { model: project, gitRef, gitRefValidationError } = projectContext.state;
    const navigateToGitRef = (gitRef: string) => {
        spaceNavigation.navigate(links.branchVariablePreviewPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }));
    };
    if (gitRefValidationError && gitRefValidationError.Type !== "Success") {
        return (<PageContent header={{ title: pageTitle }}>
                <VcsError project={project} error={gitRefValidationError} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>
            </PageContent>);
    }
    return <SharedVariablePreviewPageInternal projectContext={projectContext} ephemeralEnvironmentsAreEnabled={ephemeralEnvironmentsAreEnabled}/>;
}
export default SharedVariablePreviewPage;
